/**
 * AuthContext - Global authentication state provider.
 * Wraps the useAuth hook and adds user tier functionality.
 * Centralizes API auth initialization for all services.
 */

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, UseAuthReturn, User } from '@/hooks/useAuth';
import { setLanguagePreference, SUPPORTED_LANGUAGES, SupportedLanguageCode } from '@/i18n';
import { getAppModePreference, setAppModePreference, isMobileDevice } from '@/lib/appMode';

// API services — auth is initialized centrally here
import { settingsApi } from '@/services/settingsApi';
import { accountApi } from '@/services/accountApi';
import { avatarApi } from '@/services/avatarApi';
import { capabilitiesApi } from '@/services/capabilitiesApi';
import { addApi } from '@/services/addApi';
import { documentsApi } from '@/services/documentsApi';
import { projectsApi } from '@/services/projectsApi';
import { templatesApi } from '@/services/templatesApi';
import { sharingApi } from '@/services/sharingApi';
import { plansApi } from '@/services/plansApi';
import { stripeApi } from '@/services/stripeApi';
import { adminApi } from '@/services/adminApi';
import { systemPromptsApi } from '@/services/systemPromptsApi';
import { projectAnalysisApi } from '@/services/projectAnalysisApi';
import { draftsApi } from '@/services/draftsApi';

export type UserTier = 'guest' | 'free' | 'starter' | 'pro' | 'enterprise';

export interface AuthContextValue extends UseAuthReturn {
  userTier: UserTier;
  isAdmin: boolean;
  appMode: boolean;
  setAppMode: (enabled: boolean) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Determine user tier from Cognito user attributes.
 */
function getUserTier(user: User | null, isAuthenticated: boolean): UserTier {
  if (!isAuthenticated || !user) {
    return 'guest';
  }

  const plan = user.plan?.toLowerCase();
  if (plan === 'enterprise') return 'enterprise';
  if (plan === 'pro') return 'pro';
  if (plan === 'starter') return 'starter';
  return 'free';
}

/**
 * Initialize all authenticated API services with the access token getter.
 * Called once when the user authenticates.
 */
function initializeApiAuth(getAccessToken: () => Promise<string | null>) {
  settingsApi.setAuthProvider(getAccessToken);
  accountApi.setAuthProvider(getAccessToken);
  avatarApi.setAuthProvider(getAccessToken);
  capabilitiesApi.setAuthProvider(getAccessToken);
  addApi.setAuthProvider(getAccessToken);
  documentsApi.setAuthProvider(getAccessToken);
  projectsApi.setAuthProvider(getAccessToken);
  templatesApi.setAuthProvider(getAccessToken);
  sharingApi.setAuthProvider(getAccessToken);
  plansApi.setAuthProvider(getAccessToken);
  stripeApi.setAuthProvider(getAccessToken);
  adminApi.setAuthProvider(getAccessToken);
  systemPromptsApi.setAuthProvider(getAccessToken);
  projectAnalysisApi.setAuthProvider(getAccessToken);
  draftsApi.setAuthProvider(getAccessToken);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [appMode, setAppMode] = useState<boolean>(getAppModePreference());

  const userTier = useMemo(() => {
    return getUserTier(auth.user, auth.isAuthenticated);
  }, [auth.user, auth.isAuthenticated]);

  // Initialize API auth synchronously during render so it completes before
  // any child useEffect fires (React runs child effects before parent effects).
  const authInitRef = useRef(false);
  if (auth.isAuthenticated && !auth.isLoading) {
    if (!authInitRef.current) {
      initializeApiAuth(auth.getAccessToken);
      authInitRef.current = true;
    }
  } else if (authInitRef.current) {
    authInitRef.current = false;
  }

  // Load user preferences and sync profile after authentication
  useEffect(() => {
    if (!auth.isAuthenticated || auth.isLoading) return;

    // Sync user profile to DynamoDB (fire-and-forget) so backend functions
    // can look up email/plan when using access token authentication
    if (auth.user) {
      settingsApi.syncProfile({
        email: auth.user.email,
        plan: auth.user.plan || 'free',
        fullName: auth.user.fullName || '',
      }).catch(() => {
        // Best-effort — profile sync failure doesn't block the app
      });
    }

    settingsApi.getUserSettings()
      .then((settings) => {
        if (settings?.language && SUPPORTED_LANGUAGES.some(l => l.code === settings.language)) {
          setLanguagePreference(settings.language as SupportedLanguageCode);
        }
        if (settings?.appMode !== undefined) {
          setAppMode(settings.appMode);
          setAppModePreference(settings.appMode);
        } else if (isMobileDevice()) {
          // Auto-enable app mode on mobile devices when no explicit preference is saved
          setAppMode(true);
          setAppModePreference(true);
        }
      })
      .catch(() => {
        // Silently ignore — falls back to localStorage/default
      });
  }, [auth.isAuthenticated, auth.isLoading, auth.user, auth.getAccessToken]);

  // Check if user is in the admins group
  const isAdmin = useMemo(() => {
    return auth.user?.groups?.includes('admins') ?? false;
  }, [auth.user?.groups]);

  const value = useMemo<AuthContextValue>(() => ({
    ...auth,
    userTier,
    isAdmin,
    appMode,
    setAppMode,
  }), [auth, userTier, isAdmin, appMode, setAppMode]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
